<template>
  <div v-if="feedbackMessage.message !== ''">
    <Message closable class="mb-2" role="alert" :severity="feedbackMessageSeverity">
      {{ feedbackMessage.message }}
    </Message>
  </div>
  <div id="wdw-map-project" ref="wdwMapProjectEl">
    <!-- :style="widgetHeight" -->
    <div class="row">
      <div class="col-xs-12" :class="customColMapClass" :style="mapHeight">
        <ol-map
          v-if="projectAvailable"
          customButtonRenderer="thought"
          :domainid="domainid"
          :config="mapConfig(project)"
          :height="height"
          :initial_lat="showThoughtPosition.lat"
          :initial_lon="showThoughtPosition.lon"
          :initial_zoom="showThoughtPosition.zoom"
          :showMarkerAtInitialPosition="showThoughtPosition.customMarkerUri"
          :sizeChanged="sizeChanged"
          :refreshMarkers="refreshThoughts"
          :filterParams="filterParams"
          :setMarkerAllowed="allowSettingMarker"
          :showPopupAllowed="!allowSettingMarker"
          :hideNewMarkerLayer="!createThoughtLocation"
          :useDeviceLocation="true"
          :emitWarningInstead="true"
          @new-marker-position="markerPositionChanged"
          @open-feature="openThought"
          @warn-marker-position="markerPositionWarned"
        />
        <div
          class="map-overlay d-flex flex-row align-items-center"
          :class="{ 'with-create': canCreateThoughtAnyTopic || canCreateThoughtAnyTopicAfterLogin }"
        >
          <template v-if="canCreateThoughtAnyTopic || canCreateThoughtAnyTopicAfterLogin">
            <div class="create-btn">
              <button
                type="button"
                class="btn btn-primary btn-create-thought"
                autocomplete="off"
                @click="openCreateThoughtForm"
              >
                {{ domainStore.translate('thought.create.btn-title') }}
                <font-awesome-icon icon="plus" style="vertical-align: text-top" />
              </button>
            </div>
          </template>

          <map-search-address @addressCoordinatesReceived="setMapCenter" />

          <OverlayPanel
            v-if="!isLoggedIn"
            ref="loginOPcreate"
            :showCloseIcon="true"
            :dismissable="true"
            appendTo="body"
            class="wdw-widgets"
          >
            <p>{{ domainStore.translate('thought.create.requires-login') }}</p>
            <Login :domainid="domainid" />
          </OverlayPanel>
        </div>
      </div>
      <div
        v-show="showMiddleCol"
        class="thought-detail-wrapper"
        :class="customColClass"
        :style="colElemHeight"
        ref="thoughtDetailWrapper"
      >
        <template v-if="showDetailThoughtId && !showThoughtForm">
          <thought-wrapper
            :thoughtid="showDetailThoughtId"
            :actions="wrapperActions"
            @close-thought="closeThought"
            @edit-thought="editThought"
            @set-map-center="setMapCenter"
          />
        </template>
        <template v-if="showThoughtForm">
          <template v-if="showDetailThoughtId && createInTopic">
            <!-- in edit -->
            <thought-form
              :topic="createInTopic"
              :thoughtid="showDetailThoughtId"
              :attributeDefaultValues="overrideFormDefaultValues"
              @feedback-thought="onFeedbackFromThought"
              @close-thought="closeThought"
              @cancel-thought-form="cancelThoughtForm"
              @set-map-center="setMapCenter"
            />
          </template>
          <template v-else>
            <!-- in create -->
            <template v-if="createInTopic || availableLocationTopicsForThoughts.length > 0">
              <div class="menu-buttons">
                <button type="button" class="btn btn-primary btn-back" @click="resetTopicSelection">Zurück</button>
                <button
                  v-if="createThoughtLocation"
                  type="button"
                  class="btn btn-primary btn-reposition-thought float-end"
                  :class="{ active: changingPosition }"
                  data-mdb-toggle="button"
                  autocomplete="off"
                  :aria-pressed="changingPosition"
                  @click="changingPosition = !changingPosition"
                >
                  Position korrigieren
                </button>
              </div>
              <div v-if="changingPosition" class="alert alert-info">
                Bitte setzen Sie durch Klicken auf die Karte eine neue Position.
              </div>
              <div v-if="positionChangeMessage" class="alert alert-info">
                {{ positionChangeMessage }}
              </div>
              <template v-if="canCreateThought(createInTopic) || availableLocationTopicsForThoughts.length > 0">
                <div id="wdw-bms-warn-position" style="visibility: hidden"></div>
                <thought-form-choose-topic
                  v-if="availableLocationTopicsForThoughts.length > 1 && !showDetailThoughtId"
                  :avalibleTopics="availableLocationTopicsForThoughts"
                  @topic-selected="selectedTopicForForm"
                />
                <template v-if="createInTopic">
                  <h3>{{ domainStore.translate('thought.create.title') }}: "{{ createInTopic.title }}"</h3>
                  <thought-form
                    :topic="createInTopic"
                    :geolocation="createThoughtLocation"
                    :disableForm="changingPosition"
                    @feedback-thought="onFeedbackFromThought"
                    @close-thought="closeThought"
                    @cancel-thought-form="cancelThoughtForm"
                    @set-map-center="setMapCenter"
                    :labelTitle="thoughtFormTitleLabel"
                    :labelDescription="thoughtFormDescriptionLabel"
                    :attributeDefaultValues="overrideFormDefaultValues"
                  />
                </template>
              </template>
              <div v-else-if="canCreateThoughtAfterLogin(createInTopic) && !changingPosition">
                <p class="alert alert-info">{{ domainStore.translate('thought.create.requires-login') }}</p>
                <login :domainid="domainid"></login>
              </div>
            </template>
            <template v-else>
              <div class="alert alert-info">
                {{ domainStore.translate('thought.create.crowdmapping.select-position') }}
              </div>

              <template v-if="onCreateLoading">
                <vue-loading loadingMessage="Ausgewählte Position wird geladen..." />
              </template>
              <template v-else>
                <template v-if="noTopicAtLocation">
                  <p>
                    {{ domainStore.translate('thought.create.crowdmapping.invalid-position') }}
                    Zoomen Sie ggf. weiter in die Karte hinein, um den gewünschten Ort präziser auswählen zu können.
                  </p>
                </template>
                <template v-if="topicWithoutLocation">
                  <p>{{ domainStore.translate('thought.create.crowdmapping.without-position-question') }}</p>
                  <template v-if="topicsWithoutLocation && topicsWithoutLocation.length > 1">
                    <thought-form-choose-topic
                      :avalibleTopics="topicsWithoutLocation"
                      appendLabel="ohne Ortsbezug"
                      @topic-selected="selectedTopicForForm"
                    />
                  </template>
                  <template v-else>
                    <p>
                      <button
                        type="button"
                        class="btn btn-primary btn-thought-without-position"
                        @click="selectTopicWithoutPosition"
                      >
                        {{ domainStore.translate('thought.create.crowdmapping.without-position-btn-title') }}
                      </button>
                      <OverlayPanel
                        v-if="!isLoggedIn"
                        ref="loginOPnoLoc"
                        :showCloseIcon="true"
                        :dismissable="true"
                        appendTo="body"
                        class="wdw-widgets"
                      >
                        <p>{{ domainStore.translate('thought.create.requires-login') }}</p>
                        <Login :domainid="domainid" />
                      </OverlayPanel>
                    </p>
                  </template>
                </template>
              </template>

              <div>
                <button
                  type="button"
                  class="btn btn-default btn-cancel-thought-create btn-form-cancel"
                  @click="cancelThoughtForm()"
                >
                  Abbrechen
                </button>
              </div>
            </template>
          </template>
        </template>
      </div>
      <div
        v-show="showListCol"
        class="thought-list-wrapper"
        :class="customColListClass"
        :style="colElemHeight"
        ref="thoughtListWrapper"
      >
        <button
          v-show="isTwoColMode && !isListCollapsedInTwoColMode"
          @click="toggleListCollapseInTwoColMode()"
          :title="btnCollapseListTitle"
          class="btn btn-primary btn-toggle-collapse-list"
        >
          &gt;&gt;
        </button>
        <Transition name="collapse-fade">
          <div v-show="!isTwoColMode || !isListCollapsedInTwoColMode">
            <div class="thought-filter" v-if="topics.length > 0">
              <thought-filter
                :fields="defaultFilterFields"
                :attributeFields="attributeFilterFields"
                :filterAttributesDependency="filterAttributesDependency"
                :refreshList="refreshThoughts"
                @on-filter-toggled="onThoughtFilterToggled"
              />
            </div>
            <div class="thought-list" v-if="isLoading == false">
              <thought-list
                :watermark="true"
                :load_more_on_scroll="isTwoColMode"
                :config="{
                  showThumbnail: false,
                  linkThoughtBox: true,
                  hideVoting: false,
                  hideComments: true,
                  hideCommentButton: false,
                  hideFeedback: true,
                }"
                @open-thought="openThought"
                @edit-thought="editThought"
              />
            </div>
            <vue-loading v-else :loadingMessage="domainStore.translate('thought.general.message.loading-plural')" />
          </div>
        </Transition>
        <button
          ref="btnToggleCollapseList"
          v-show="isTwoColMode && isListCollapsedInTwoColMode"
          class="btn btn-primary"
          :title="btnCollapseListTitle"
          style="height: 100%"
          @click="toggleListCollapseInTwoColMode()"
        >
          &lt;&lt;
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { userStore } from '@/store/user-store';
import { domainStore } from '@/store/domain-store';
import { projectStore } from '@/store/project-store';
import { thoughtStore } from '@/store/thought-store';
import useAuthentication from '@/composables/user/useAuthentication';
import { useRoute } from 'vue-router';
import { onMounted, onBeforeMount, ref, watch, computed, reactive, nextTick } from 'vue';
import OlMap from '@/components/general/OlMap.vue';
// import WdwDynbox from '@/components/general/WdwDynbox.vue';
import ThoughtFilter from '@/components/discuss/topic/ThoughtFilter.vue';
import ThoughtList from '@/components/discuss/topic/ThoughtList.vue';
import ThoughtWrapper from '@/components/discuss/thought/Wrapper.vue';
import ThoughtForm from '@/components/discuss/thought/Form.vue';
import useThoughtFilter from '@/composables/thought/useThoughtFilter';
import useTopicConfig from '@/composables/topic/useTopicConfig';
import useMapProject from '@/composables/map/useMapProject';
import useHashParams from '@/composables/general/useHashParams';
import useConverter from '@/composables/general/useConverter';
import useTopicTerm from '@/composables/topic/useTopicTerm';
import VueLoading from '@/components/general/Loading';
import Login from '@/components/user/Login.vue';
import MapSearchAddress from '@/components/general/MapSearchAddress.vue';
import ThoughtFormChooseTopic from '@/components/discuss/thought/FormChooseTopic.vue';
import OverlayPanel from 'primevue/overlaypanel';
import Message from 'primevue/message';
import { RepositoryFactory } from '@/api/repositoryFactory';
import { useElementVisibility } from '@vueuse/core';

const ProjectRepository = RepositoryFactory.get('project');

// TODO -> fully implement

export default {
  name: 'MapProject',
  props: {
    domainid: {
      type: Number,
      required: true,
    },
    projectid: {
      type: Number,
      required: true,
    },
    height: {
      type: Number,
      default: 500,
    },
    twoColBreakpoint: {
      type: Number,
      default: 1600,
    },
    force_mobile_layout: {
      type: Boolean,
      default: false,
      required: false,
    },
  },

  setup(props) {
    /**
     * Per T18783, thought-filter will be shown AFTER the list of topics is retrieved
     * the reason is that every topic can have its own ordering and the thought-filter always downloads the list of topics before the API call resolves
     * Assumption: Every project always have at least 1 topic
     */
    const { getVirtualUser } = useAuthentication();
    const { getDefaultFilterFields, getSortOptions, getThoughtWeightField } = useThoughtFilter();
    const { canCreateThought, canCreateThoughtAfterLogin } = useTopicConfig();
    const { mapConfig } = useMapProject();
    const route = useRoute();
    const { deleteHashParam, setHashParams } = useHashParams(route);
    const { toBool } = useConverter();
    const { topicLabel } = useTopicTerm();
    let lock = true;
    const defaultFilterFields = ref({});
    const attributeFilterFields = ref([]);
    const filterAttributesDependency = ref({});
    // add specific topic filter to filter fields
    defaultFilterFields.value = getDefaultFilterFields();
    defaultFilterFields.value.projectid = {
      name: 'Projekt',
      type: 'number',
      default_value: props.projectid,
      visible: false,
    };

    // element / overlay references
    const loginOPcreate = ref();
    const loginOPnoLoc = ref();
    const wdwMapProjectEl = ref();
    const thoughtListWrapper = ref(null); // handle refetching thoughts when scrolling inside fixed-height elem
    const thoughtDetailWrapper = ref(null); // for scrolling to element when opening detail
    const btnToggleCollapseList = ref(null); // toggle button for collapse/show list

    const componentWidth = ref(0); //updated on start and after showing/hiding middle column
    const isListCollapsedInTwoColMode = ref(false); // State for whether the list is collapsed in twoColMode

    // vars for functionality / creating&editing thought
    const showDetailThoughtId = ref(null);
    const showThoughtPosition = ref({ lat: 0, lon: 0, zoom: 0, customMarkerUri: '' });
    const showThoughtForm = ref(false);
    const sizeChanged = ref(0); // to trigger ol-map resizing, just count up, any var-change works
    const refreshThoughts = ref(0); // to trigger refreshing markers/list after new thought

    const topics = ref([]);
    const createInTopic = ref(null);
    const availableLocationTopics = ref([]);
    const createThoughtLocation = ref(null);
    const onCreateLoading = ref(false);
    const noTopicAtLocation = ref(false);
    const changingPosition = ref(false);
    const positionChangeMessage = ref('');
    const wrapperActions = ref({});
    const initCollapseListInTwoColMode = ref(false);
    const feedbackMessage = reactive({
      message: '',
      type: '',
    });
    // Per T17350, default values in thought form can be overridden
    const overrideFormDefaultValues = ref({});
    // T18783: filter by chosen topicid thought hash parameter
    const filteredTopicIdFromHash = ref(0);

    function toggleListCollapseInTwoColMode() {
      // Toggle collapse and trigger size change on map so it re-renders
      isListCollapsedInTwoColMode.value = !isListCollapsedInTwoColMode.value;
      sizeChanged.value = sizeChanged.value + 1;
    }

    // computed
    const user = computed(() => {
      return userStore.getState().user;
    });
    const isLoggedIn = computed(() => {
      return userStore.isLoggedIn();
    });
    const isLoading = computed(() => {
      if (lock) {
        return userStore.isLoading();
      } else {
        return false;
      }
    });
    const domain = computed(() => {
      return domainStore.getState().domain;
    });
    const project = computed(() => {
      return projectStore.getState().project;
    });
    const projectAvailable = computed(() => {
      if (project.value) {
        return project.value.id ? true : false;
      }
      return false;
    });
    const showMiddleCol = computed(() => {
      return (showDetailThoughtId.value && showDetailThoughtId.value > 0) || showThoughtForm.value;
    });
    const mobileBreakpoint = computed(() => {
      if (props.force_mobile_layout) return true;
      return componentWidth.value <= 991;
    });
    const showListCol = computed(() => {
      // if above breakpoint, always show list column
      // else only show when middle column is not visible
      return !showMiddleCol.value || mobileBreakpoint.value || componentWidth.value >= props.twoColBreakpoint;
    });
    const isTwoColMode = computed(() => {
      // Check if the map and list are displayed in 2 columns
      return !mobileBreakpoint.value;
    });
    const customColClass = computed(() => {
      let classes = 'col-lg-4';
      if (showMiddleCol.value) {
        // If middle column is showing, set the width depending if the list is collapsed or not
        classes = isListCollapsedInTwoColMode.value ? 'col-lg-5' : 'col-lg-4';
      }

      if (!showListCol.value) {
        // In 2-col mode, just expand to half page
        classes = 'col-lg-6';
      }

      if (mobileBreakpoint.value) {
        classes = 'col-lg-12 mobile'; // enforce lg-12 if below mobile breakpoint
      }
      return classes;
    });
    const customColMapClass = computed(() => {
      let classes = 'col-lg-6';
      if (showMiddleCol.value) {
        // If middle col (detail) is showing, 1/3rd if list is not collapsed, half if collapsed
        classes = isListCollapsedInTwoColMode.value ? 'col-lg-6' : 'col-lg-4';
      } else {
        // If middle col (detail) is not showing, Almost full width if collapsed, half if not
        classes = isListCollapsedInTwoColMode.value ? 'col-lg-11' : 'col-lg-6';
      }

      if (!showListCol.value) {
        // in 2-col-mode, set map to half width
        classes = 'col-lg-6';
      }

      if (mobileBreakpoint.value) {
        classes = 'col-lg-12 mobile'; // enforce lg-12 if below mobile breakpoint
      }
      return classes;
    });
    const customColListClass = computed(() => {
      let classes = '';
      if (isListCollapsedInTwoColMode.value) {
        // If list is collapsed, set to 1 unit of width
        classes = 'col-lg-1';
      } else {
        // If list is not collapsed, set to half page if mid column is not showing or 1/3rd otherwise
        classes = showMiddleCol.value ? 'col-lg-4' : 'col-lg-6';
      }

      if (mobileBreakpoint.value) {
        classes = 'col-lg-12 mobile'; // enforce lg-12 if below mobile breakpoint
      }
      return classes;
    });
    const btnCollapseListTitle = computed(() => {
      return isListCollapsedInTwoColMode.value ? 'Liste einblenden' : 'Liste ausblenden';
    });
    const mapHeight = computed(() => {
      return `height:${props.height}px`;
    });
    const colElemHeight = computed(() => {
      if (mobileBreakpoint.value) {
        // mobile breakpoint
        return 'height:auto;';
      }
      return `height:${props.height}px`;
    });
    const thoughtsLoading = computed(() => {
      return thoughtStore.getState().loading;
    });
    const filterParams = computed(() => {
      return thoughtStore.getState().thoughtFilter;
    });
    const canCreateThoughtAnyTopic = computed(() => {
      if (topics.value.length == 0) return false;
      return topics.value.some(el => canCreateThought(el));
    });
    const canCreateThoughtAnyTopicAfterLogin = computed(() => {
      if (topics.value.length == 0) return false;
      return topics.value.some(el => canCreateThoughtAfterLogin(el));
    });
    const topicsWithoutLocation = computed(() => {
      return topics.value.filter(
        el => el.has_location !== 'required' && (canCreateThought(el) || canCreateThoughtAfterLogin(el))
      );
    });
    const topicWithoutLocation = computed(() => {
      if (topicsWithoutLocation.value && topicsWithoutLocation.value.length > 0) {
        return topicsWithoutLocation.value[0];
      }
      return null;
    });
    const allowSettingMarker = computed(() => {
      if (showThoughtForm.value && showMiddleCol.value) {
        if (!createInTopic.value || changingPosition.value) {
          return true;
        }
      }
      return false;
    });
    const feedbackMessageSeverity = computed(() => {
      return feedbackMessage.type || 'info';
    });
    const thoughtFormTitleLabel = computed(() => {
      return project.value
        ? project.value.thought_title_label
        : domainStore.translate('thought.create.form.title-label');
    });
    const thoughtFormDescriptionLabel = computed(() => {
      return project.value
        ? project.value.thought_content_label
        : domainStore.translate('thought.create.form.description-label');
    });
    const availableLocationTopicsForThoughts = computed(() => {
      return availableLocationTopics.value.filter(topic => canCreateThought(topic));
    });

    // watch on project in store to setup filter values after project was fetched
    watch(
      () => projectStore.getState().project,
      (project, oldProject) => {
        if (project !== null && project.id === props.projectid) {
          // If at least 1 topic has votetype = weighted_support, we need to add the weight sort options
          const isWeightedSupportType = project.topics && project.topics.find(x => x.votetype == 'weighted_support');
          const sortOpts = getSortOptions({
            votetype: isWeightedSupportType ? 'weighted_support' : 'support',
            default_sort: 'random',
            commentcount: 0, // commentcount comes below with topics
            new_comments: false,
          });
          defaultFilterFields.value.sort.default_value = sortOpts.default;
          defaultFilterFields.value.sort.values = sortOpts.values;

          if (project.config) {
            const weightField = getThoughtWeightField(project.topics, project.config.weighted_support_point_map);
            if (weightField != null) {
              defaultFilterFields.value.vote_weight = weightField;
            }
          }

          if (project.topics && project.topics.length > 1) {
            const options = [];
            project.topics.forEach(t => {
              options.push({
                id: t.id + '',
                ordering: 1,
                text: t.title,
              });
            });
            defaultFilterFields.value.topicid = {
              name: topicLabel(),
              type: 'valuelist',
              default_value: 0,
              visible: true,
              values: options,
              allowClear: true,
            };
          }
        }

        if (!userStore.getState().authToken && process.env.VUE_APP_ENABLE_VIRTUAL_USER) {
          // try to init/get virtual user for eventual anonymous votes/comments/thoughts
          getVirtualUser()
            .then(success => {
              // console.log('initialized virtual user');
            })
            .catch(e => {
              console.log('error getting virtual user');
            });
        }
      }
    );

    watch(
      () => projectStore.getState().topics,
      (newTopics, oldTopics) => {
        if (newTopics !== null && newTopics.length > 0) {
          topics.value = projectStore.getTopics();

          // TODO
          // [x] init attribute fields from entire project
          // [ ] init sort options from entire project
          const topic_sortOptions = {};
          filterAttributesDependency.value = { topicid: {} };
          // update attribute fields for filter

          // Per T18783: use chosen topic if it's defined
          let filteredTopic = null;
          if (filteredTopicIdFromHash.value > 0) {
            filteredTopic = newTopics.find(t => t.id == filteredTopicIdFromHash.value);
          }

          newTopics.forEach(t => {
            filterAttributesDependency.value.topicid[t.id] = [];
            if (t.attributes) {
              t.attributes.forEach(el => {
                if (toBool(el.relation_data.show_in_filter)) {
                  // clone element to be able to edit it for filter
                  const elCopy = JSON.parse(JSON.stringify(el));
                  elCopy.filter_name = 'attribute' + el.id;
                  elCopy.visible = true;
                  if (!attributeFilterFields.value.find(attr => attr.id === elCopy.id)) {
                    attributeFilterFields.value.push(elCopy);
                  }
                  filterAttributesDependency.value.topicid[t.id].push(elCopy);
                }
              });

              const sortOpts = getSortOptions({
                votetype: t.votetype,
                default_sort: t.default_sort,
                commentcount: t.commentcount,
                new_comments: t.new_comments,
              });
              topic_sortOptions[t.id] = sortOpts;

              if (
                sortOpts.values.find(x => x.id == 'commentcount') &&
                !defaultFilterFields.value.sort.values.find(x => x.id == 'commentcount')
              ) {
                defaultFilterFields.value.sort.values.push(sortOpts.values.find(x => x.id == 'commentcount'));
              }
            }
          });

          const sortOptions = Object.entries(topic_sortOptions);
          // If a topic is preselected, this would have the filtered topic's sort option
          const sortOptionOnPreselectedTopic = filteredTopic ? sortOptions.find(x => x[0] == filteredTopic.id) : null;

          let topicid = null;
          if (sortOptionOnPreselectedTopic) {
            // sortOptionOnPreselectedTopic should be [ <topicid>, <hash-of-sort-options> ]
            // Per T18783, if there is only one entry for sort options, use that
            // Example: When it is filtered by topicid through hash param
            defaultFilterFields.value.sort.values = sortOptionOnPreselectedTopic[1].values;
            defaultFilterFields.value.sort.default_value = sortOptionOnPreselectedTopic[1].default;
            // Set chosen topicid
            topicid = sortOptions[0][0];
          }

          const filterParams = {
            sort: defaultFilterFields.value.sort.default_value,
            topicid: topicid,
            projectid: props.projectid,
          };
          // set default values for filter
          // filterStore.setDefaultValues(filterParams);
          // Update the thought filter value for filtering
          thoughtStore.updateThoughtFilter(filterParams);
          // Reload the thoughts anyway
          // T22381: Don't reload thoughts! Let ThoughtFilter reloads them since filter values can come from hash param
          // thoughtStore.reloadThoughts();

          // TODO figure out how to define sort options over multiple topics
        }
      }
    );

    function onThoughtFilterToggled(isMinimized) {
      const filterElems = document.getElementsByClassName('thought-filter');
      for (let i = 0; i < filterElems.length; i++) {
        const elem = filterElems.item(i);
        elem.style.position = isMinimized ? 'sticky' : 'static';
      }
    }

    function setMapCenter(location, zoom = 18) {
      if (location.zoom) {
        zoom = location.zoom;
      }
      showThoughtPosition.value = { lat: location.lat, lon: location.lon, zoom: zoom, customMarkerUri: '' };
      if (location.marker_uri) {
        showThoughtPosition.value.customMarkerUri = location.marker_uri;
      }
    }

    function resetMapCenter() {
      if (project.value && project.value.map_settings) {
        // Per T17159, reset position back to the initial map settings taken from project
        const mapsettings = project.value.map_settings;
        showThoughtPosition.value = {
          lat: mapsettings.position.lat,
          lon: mapsettings.position.lon,
          zoom: mapsettings.zoom.default,
          customMarkerUri: '',
        };
      }
    }

    function resetTopicSelection() {
      // TODO
      //  - prüfen ob fields disablen während positionsänderung?
      onCreateLoading.value = false;
      noTopicAtLocation.value = false;
      createInTopic.value = null;
      availableLocationTopics.value = [];
      overrideFormDefaultValues.value = {};
    }
    function clearFeedback() {
      // TODO when to clear?
      feedbackMessage.message = '';
      feedbackMessage.type = '';
    }

    function confirmClosingForm() {
      return confirm('Möchten Sie die Eingabe abbrechen?');
    }

    // regularly update width of component
    function recalcComponentWidth() {
      componentWidth.value = wdwMapProjectEl.value.clientWidth;
    }

    function openThought(thoughtid, topicid, actions) {
      // console.log('open');
      // also force open infobox in map when opening thought?
      let openThought = true;

      if (showThoughtForm.value) {
        // double check if we should close a form
        openThought = confirmClosingForm();
      }

      if (openThought) {
        if (actions) {
          wrapperActions.value = actions;
        } else {
          wrapperActions.value = {};
        }
        showThoughtForm.value = false;
        showDetailThoughtId.value = thoughtid;
        sizeChanged.value = sizeChanged.value + 1;
        // T18783: has to be set to false so the filter from filter form remains after you go back to list
        setHashParams({ thought_id: thoughtid }, false);
      }

      // update component width, to switch between two and three column layout
      recalcComponentWidth();

      // 500ms timeout for the reference to mount
      setTimeout(() => {
        if (thoughtDetailWrapper.value) {
          thoughtDetailWrapper.value.scrollIntoView({ block: 'start', behavior: 'smooth' });
        } else if (thoughtListWrapper.value) {
          thoughtListWrapper.value.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
      }, 500);
    }

    function editThought(thoughtid, topicid) {
      // console.log('open-edit', showThoughtForm.value);

      let openThought = true;

      if (showThoughtForm.value) {
        // double check if we should close a form
        openThought = confirmClosingForm();
      }

      if (openThought) {
        showDetailThoughtId.value = thoughtid;
        createInTopic.value = projectStore.getFetchedTopic(topicid);
        showThoughtForm.value = true;
        sizeChanged.value = sizeChanged.value + 1;
      }

      // update component width, to switch between two and three column layout
      recalcComponentWidth();
    }

    function openCreateThoughtForm(event) {
      //Check if user is logged in
      if (canCreateThoughtAnyTopic.value) {
        clearFeedback();
        showThoughtForm.value = true;
        showDetailThoughtId.value = null;
        sizeChanged.value = sizeChanged.value + 1;
        positionChangeMessage.value = '';
      } else if (canCreateThoughtAnyTopicAfterLogin.value) {
        loginOPcreate.value.show(event);
      } else {
        console.error('error: cannot create thought, but button visible/clicked');
      }

      // update component width, to switch between two and three column layout
      recalcComponentWidth();
    }

    function selectTopicWithoutPosition(event) {
      if (canCreateThought(topicWithoutLocation.value)) {
        onCreateLoading.value = false;
        noTopicAtLocation.value = false;
        availableLocationTopics.value = [];
        createInTopic.value = topicWithoutLocation.value;
        createThoughtLocation.value = null;
      } else if (canCreateThoughtAfterLogin(topicWithoutLocation.value)) {
        loginOPnoLoc.value.show(event);
      } else {
        console.error('error: cannot create thought, but button visible/clicked');
      }
    }

    function closeThought(params = {}) {
      let force = false;
      if (params['force'] === true) {
        force = true;
      }
      let closeThought = true;

      if (showThoughtForm.value && !force) {
        // double check if we should close a form
        closeThought = confirmClosingForm();
      }

      if (closeThought) {
        // Also need to be set to false so the list does not refresh after going back from detail view
        // while also retaining the last filter setting
        deleteHashParam('thought_id', false);
        showDetailThoughtId.value = null;
        sizeChanged.value = sizeChanged.value + 1;
        showThoughtForm.value = false;
        createThoughtLocation.value = null;
        changingPosition.value = false;
        positionChangeMessage.value = '';
        resetTopicSelection();
        resetMapCenter();

        if (params['jumpToMap'] && wdwMapProjectEl.value) {
          wdwMapProjectEl.value.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
      }

      overrideFormDefaultValues.value = {};

      // update component width, to switch between two and three column layout
      recalcComponentWidth();
    }

    function onFeedbackFromThought(feedback) {
      // console.log("in feedback event map");
      showThoughtForm.value = false;
      createInTopic.value = null;
      noTopicAtLocation.value = false;
      createThoughtLocation.value = null;
      // todo
      // check if we can display thought correctly first
      // always assume we can show the thought to the user that created it??
      /**
       * T19022: reloadThoughts has to complete first before fetching the created thought.
       *      Otherwise, it will clear out the thought store after thought fetching resolves,
       *      resulting in empty array
       */
      if (feedback.thought.id) {
        thoughtStore.reloadThoughts(function () {
          openThought(feedback.thought.id);
        });
      } else {
        closeThought({ force: true });
      }
      feedbackMessage.message = feedback.message;
      feedbackMessage.type = feedback.type;

      // update component width, to switch between two and three column layout
      recalcComponentWidth();
    }

    function cancelThoughtForm() {
      if (showDetailThoughtId.value) {
        // go back to detail view without form
        showThoughtForm.value = false;
      } else {
        // force close create form entirely (close middle column)
        closeThought({ force: true });
      }
      // update component width, to switch between two and three column layout
      recalcComponentWidth();
    }

    function markerPositionWarned(param) {
      const warnElem = document.getElementById('wdw-bms-warn-position');
      if (warnElem) {
        warnElem.innerHTML = "<p class='alert alert-warning'>" + param.text + '</p>';
        warnElem.style.visibility = 'visible';
      }
    }

    function selectedTopicForForm(topic) {
      createInTopic.value = topic;
    }

    function markerPositionChanged(location) {
      onCreateLoading.value = true;
      noTopicAtLocation.value = false;
      createThoughtLocation.value = location;
      positionChangeMessage.value = '';
      ProjectRepository.getTopicsAtGeoposition(props.projectid, location.lon, location.lat, 'config')
        .then(topics => {
          const valid_topics = topics.filter(topic => canCreateThought(topic) || canCreateThoughtAfterLogin(topic));
          if (valid_topics.length > 1) {
            // createInTopic.value = topic;
            availableLocationTopics.value = valid_topics;

            // TODO logic here
            changingPosition.value = false;
          } else if (valid_topics.length == 1) {
            createInTopic.value = valid_topics[0];
            changingPosition.value = false;

            /**
             * Per T17350, passed parameter might have default values that can override the ones in thought form
             *    additional_data may also have property `hide_on_initial_value` (1 or 0).
             *    If this is active, fields that get their default value will be hidden to prevent further changes
             */

            if (location.additional_data) {
              overrideFormDefaultValues.value = location.additional_data;
            } else {
              overrideFormDefaultValues.value = {};
            }
          } else {
            if (createInTopic.value && createInTopic.value.id) {
              positionChangeMessage.value =
                'An der gewählten Position ist keine Eingabe mit Ortsbezug möglich. Bitte wählen Sie eine andere Position';
            } else {
              changingPosition.value = false;
            }
            availableLocationTopics.value = [];
            createInTopic.value = null;
            noTopicAtLocation.value = true;
          }
          onCreateLoading.value = false;
        })
        .catch(error => {
          console.log(error);
        });
    }

    // check hashparams here
    watch(route, () => {
      const thought_id = parseInt(route.query.thought_id);
      if (thought_id && showDetailThoughtId.value != thought_id) {
        openThought(thought_id);
      }

      const topicid = route.query.topic_id || route.query.topicid;
      if (route.query.topic_id) {
        // Switch to topicid and remove topic_id
        // deleteHashParam("topic_id");
        setHashParams({ topicid: topicid, topic_id: null });
      }

      if (topicid) {
        // Chosen topic will be set in `watch(projectStore.getState().topics)`
        filteredTopicIdFromHash.value = topicid;
      }
    });

    watch(isLoading, (newValue, OldValue) => {
      if (isLoading.value == false && lock) {
        lock = false;
      }
    });

    watch(useElementVisibility(thoughtDetailWrapper), (newVisible, oldVisible) => {
      if (newVisible && !oldVisible && thoughtDetailWrapper.value) {
        // get first focusable element inside target to focues
        thoughtDetailWrapper.value
          .querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')[0]
          .focus();
      }
    });

    // set props to store for initialization
    onBeforeMount(() => {
      domainStore.setDomainId(props.domainid);
      projectStore.setProjectId(props.projectid, true).then(proj => {
        if (proj.config) {
          if ('thought_list_rows' in proj.config) {
            thoughtStore.setRows(parseInt(proj.config.thought_list_rows));
          }
          // T22606
          if (
            'map_init_collapse_list_in_two_col_mode' in proj.config &&
            toBool(proj.config.map_init_collapse_list_in_two_col_mode)
          ) {
            initCollapseListInTwoColMode.value = true;
            toggleListCollapseInTwoColMode();

            // Start watching visibility of map after we get the config
            watch(useElementVisibility(wdwMapProjectEl), (newV, oldV) => {
              // If list collapsed when map comes into view, blink the uncollapse button
              if (
                newV && // Map comes into view
                isListCollapsedInTwoColMode.value && // List is collapsed
                initCollapseListInTwoColMode.value && // list should be collapsed on init
                btnToggleCollapseList.value && // toggle button exists
                !btnToggleCollapseList.value.classList.contains('blink-twice') // toggle button hasn't blinked
              ) {
                setTimeout(() => {
                  btnToggleCollapseList.value.classList.add('blink-twice');
                }, 500);
              }
            });
          }
        }
      });
    });

    onMounted(() => {
      if (isLoading.value == false) {
        lock = false;
      }

      // get component width, to switch between two and three column layout
      recalcComponentWidth();
    });

    return {
      // computed vars
      isLoggedIn,
      user,
      project,
      projectAvailable,
      wrapperActions,
      mapConfig,
      customColClass,
      customColMapClass,
      customColListClass,
      isTwoColMode,
      btnCollapseListTitle,
      showMiddleCol,
      defaultFilterFields,
      attributeFilterFields,
      filterAttributesDependency,
      isLoading,

      openThought,
      closeThought,
      onFeedbackFromThought,
      showDetailThoughtId,
      mapHeight,
      colElemHeight,
      thoughtListWrapper,
      thoughtDetailWrapper,
      showThoughtPosition,
      setMapCenter,
      sizeChanged,
      refreshThoughts,
      openCreateThoughtForm,
      showThoughtForm,
      cancelThoughtForm,
      editThought,
      markerPositionChanged,
      markerPositionWarned,
      createInTopic,
      onCreateLoading,
      noTopicAtLocation,
      topicWithoutLocation,
      topicsWithoutLocation,
      resetTopicSelection,
      selectTopicWithoutPosition,
      allowSettingMarker,
      createThoughtLocation,
      feedbackMessage,
      feedbackMessageSeverity,
      canCreateThought,
      canCreateThoughtAfterLogin,
      canCreateThoughtAnyTopic,
      canCreateThoughtAnyTopicAfterLogin,
      positionChangeMessage,
      changingPosition,
      filterParams,
      showListCol,
      componentWidth,
      mobileBreakpoint,
      thoughtFormTitleLabel,
      thoughtFormDescriptionLabel,
      availableLocationTopicsForThoughts,
      selectedTopicForForm,
      onThoughtFilterToggled,

      // elements
      loginOPcreate,
      loginOPnoLoc,
      wdwMapProjectEl,
      btnToggleCollapseList,

      // functions
      recalcComponentWidth,
      toggleListCollapseInTwoColMode,

      // Variables
      isListCollapsedInTwoColMode,
      domainStore,
      overrideFormDefaultValues,
      topics,
      domain,
    };
  },

  components: {
    OverlayPanel,
    Message,
    Login,
    OlMap,
    ThoughtFilter,
    ThoughtList,
    ThoughtWrapper,
    ThoughtForm,
    MapSearchAddress,
    VueLoading,
    ThoughtFormChooseTopic,
  },
};
</script>

<style scoped lang="scss">
.thought-list-wrapper,
.thought-detail-wrapper {
  overflow-y: scroll;

  &.mobile {
    overflow-y: visible;
  }
}

.thought-filter {
  position: sticky;
  top: 0;
  will-change: transform;
  box-shadow: 0 10px 10px -10px rgba(0, 0, 0, 0.5);
  z-index: 10;
}

.menu-buttons {
  margin-bottom: 10px;
}

.map-overlay {
  position: relative;
  background: rgba(255, 255, 255, 0.8);
  z-index: 800;
  width: 100%;
  padding: 15px;
  height: 80px;
  top: -80px;
  @media (max-width: 769px) {
    &.with-create {
      flex-wrap: wrap;
      height: 120px;
      top: -120px;
      div.create-btn {
        flex-basis: 100%;
        width: 100%;
      }
    }
  }
}

#wdw-map-project {
  background-color: var(--wdw-background-color);
  color: var(--wdw-text-color);
  scroll-margin-top: 100px;
}

.thought-detail-wrapper {
  padding-top: 10px;
  padding-bottom: 10px;
}

.collapse-fade-enter-active {
  transition: all 0.3s;
}

.collapse-fade-leave-active {
  transition: all 0.3s;
}

.collapse-fade-enter-from,
.collapse-fade-leave-to {
  transform: translateX(25px);
  opacity: 0;
}
</style>
